import ToastContainer from 'components/ToastContainer';
import React, { createContext, useCallback, useContext, useState } from 'react';

interface ToastContextData {
  addToast(toastMessage: string): void;
  removeToast(): void;
}

const ToastContext = createContext<ToastContextData>({} as ToastContextData);

const ToastProvider: React.FC = ({ children }) => {
  const [message, setMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [timeoutTime, setTimeoutTime] = useState<NodeJS.Timeout>();

  const addToast = useCallback(
    (toastMessage: string) => {
      setMessage(toastMessage);
      setShowToast(true);

      if (timeoutTime) {
        clearTimeout(timeoutTime);
      }

      const time = setTimeout(() => {
        setShowToast(false);
      }, 5000);

      setTimeoutTime(time);
    },
    [setMessage, setShowToast, setTimeoutTime, timeoutTime]
  );

  const removeToast = useCallback(() => {
    setMessage(null);
    setShowToast(false);

    if (timeoutTime) {
      clearTimeout(timeoutTime);
    }
  }, [setMessage, setShowToast, timeoutTime]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastContainer message={message} show={showToast} />
    </ToastContext.Provider>
  );
};

function useToast(): ToastContextData {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}

export { useToast, ToastProvider };
